import SVG from '@app/components/SVG';
import useAppStores from '@app/hooks/useAppStores';
import useInfraStores from '@app/hooks/useInfraStores';
import QuestionMarkIcon from '@app/images/questionMark.svg';
import RightArrowIcon from '@app/images/rightArrow.svg';
import StepSuccessIcon from '@app/images/StepSuccess.svg';
import styled from '@emotion/styled';
import SupplierRegistrationMode from '@supplierRegistration/supplierRegistrationMode';
import { observer } from 'mobx-react';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
interface NoActionRequiredFormProps {
  isIpsMode: boolean;
}

const NoActionRequiredForm: FC<NoActionRequiredFormProps> = observer(({ isIpsMode }) => {
  const { navigationStore } = useInfraStores<SupplierRegistrationMode>();
  const { supplierRegistrationCustomerDataStore } = useAppStores<SupplierRegistrationMode>();
  const { t } = useTranslation();

  return (
    <ConfirmationCard>
      <MainSection>
        <IconContainer>
          <SVG accessibilityLabel='' image={StepSuccessIcon} />
        </IconContainer>
        <TextContainer>
          <ThankYouText>{t('supplierValidation.afterRegister.nothingToDo.thankYou')}</ThankYouText>
          <ValidationText>{t('supplierValidation.afterRegister.nothingToDo.willValidate')}</ValidationText>
        </TextContainer>
      </MainSection>

      <Separator />

      <InfoSection>
        <InfoItem>
          <InfoIconContainer>
            <SVG accessibilityLabel='' image={RightArrowIcon} />
          </InfoIconContainer>

          <InfoText>{t('supplierValidation.afterRegister.nothingToDo.noActionRequired')}</InfoText>
        </InfoItem>

        <Separator />

        <InfoItem>
          <InfoIconContainer>
            <SVG accessibilityLabel='' image={RightArrowIcon} />
          </InfoIconContainer>
          <InfoText>{t('supplierValidation.afterRegister.nothingToDo.onceCompleted')}</InfoText>
        </InfoItem>
      </InfoSection>

      <Separator />

      <FAQSection>
        <FAQIconContainer>
          <SVG accessibilityLabel='' image={QuestionMarkIcon} />
        </FAQIconContainer>
        <FAQLink
          to={navigationStore.generateAccountValidationForSuppliersFAQ(
            isIpsMode,
            supplierRegistrationCustomerDataStore.currentReference?.referenceId ?? null,
          )}
        >
          {t('supplierValidation.faq')}
        </FAQLink>
      </FAQSection>
    </ConfirmationCard>
  );
});

export default NoActionRequiredForm;

const ConfirmationCard = styled.div`
  background: white;
  border-radius: 10px;
  padding: 24px 0;
  max-width: 900px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  margin: 0 auto;
`;

const MainSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  margin-left: 24px;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: var(--accent-blue-600);
  border-radius: 50%;
  flex-shrink: 0;
`;

const TextContainer = styled.div`
  flex: 1;
`;

const ThankYouText = styled.div`
  font-family: var(--title-font-family);
  font-size: 20px;
  font-weight: bold;
  color: var(--transparent-black-900);
  margin-bottom: 4px;
`;

const ValidationText = styled.div`
  font-family: var(--title-font-family);
  font-size: 16px;
  font-weight: normal;
  color: var(--transparent-black-700);
  line-height: 1.4;
`;

const Separator = styled.div`
  height: 1px;
  background: #e0e0e0;
`;

const InfoSection = styled.div`
  border-left: 5px solid #03adef;
`;

const InfoItem = styled.div`
  padding: 18px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const InfoIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const InfoText = styled.div`
  flex: 1;
  font-family: var(--title-font-family);
  font-size: 16px;
  font-weight: normal;
  color: var(--transparent-black-700);
  line-height: 1.4;

  strong {
    font-weight: bold;
  }
`;

const FAQSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
  margin-left: 24px;
`;

const FAQIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: var(--accent-blue-600);
  border-radius: 50%;
  flex-shrink: 0;
`;

const FAQLink = styled(Link)`
  font-family: var(--title-font-family);
  font-size: 16px;
  font-weight: normal;
  color: var(--accent-blue-600);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;
